import { useMemo, useEffect } from 'react';
import { useEmployeeProfilePhoenix } from '@pflegenavi/frontend/api-nursing-home';
import { useNursingHomeContext } from './NursingHomeContext';
import { useRedirectUrlContext } from './redirectUrlContext';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import type { EmployeeProfilePhoenix } from '@pflegenavi/frontend/api-nursing-home';
import { useEmployeeMode } from '@pflegenavi/shared-frontend/employee-mode';

const checkIfUserHasAccess = (
  employeeProfile: EmployeeProfilePhoenix | undefined,
  selectedNursingHomeId: string | undefined,
  roles: Array<'admin' | 'nursing-home-manager' | 'employee'> = [
    'admin',
    'nursing-home-manager',
  ]
): boolean => {
  if (!selectedNursingHomeId || !employeeProfile) {
    return false;
  }

  // If use_new_authorization is false and no resource_associations exist,
  // assume the user is a nursing home manager if that role is requested
  if (!employeeProfile.use_new_authorization) {
    const nursingHomeAssociations =
      employeeProfile.resource_associations?.['nursing_home'];
    if (!nursingHomeAssociations || nursingHomeAssociations.length === 0) {
      return roles.includes('nursing-home-manager');
    }
  }

  return Boolean(
    employeeProfile.resource_associations?.['nursing_home']?.some(
      (association) =>
        association.resource_id === selectedNursingHomeId &&
        roles.includes(association.role.name)
    )
  );
};
export function useEmployeeIsNursingHomeAdminOrManager(
  enabled: boolean = true
): boolean {
  const { data: employeeProfile } = useEmployeeProfilePhoenix({
    enabled,
  });
  const { selectedNursingHome } = useNursingHomeContext();

  return useMemo(() => {
    return checkIfUserHasAccess(employeeProfile, selectedNursingHome?.id, [
      'admin',
      'nursing-home-manager',
    ]);
  }, [employeeProfile, selectedNursingHome]);
}

export function useEmployeeIsNursingHomeAdmin(
  enabled: boolean = true
): boolean {
  const { data: employeeProfile } = useEmployeeProfilePhoenix({
    enabled,
  });
  const { selectedNursingHome } = useNursingHomeContext();

  return useMemo(() => {
    return checkIfUserHasAccess(employeeProfile, selectedNursingHome?.id, [
      'admin',
    ]);
  }, [employeeProfile, selectedNursingHome]);
}

export function useSelectedEmployeeIds() {
  const isNursingHomeAdminOrManager = useEmployeeIsNursingHomeAdminOrManager();

  const { user } = useAuthentication();

  const { selectedEmployeeId } = useEmployeeMode();
  return useMemo(() => {
    if (isNursingHomeAdminOrManager) {
      return selectedEmployeeId ? [selectedEmployeeId] : undefined;
    }
    return user?.userId ? [user.userId] : undefined;
  }, [isNursingHomeAdminOrManager, selectedEmployeeId, user?.userId]);
}

export function useRedirectUrlContextRoles(url: string) {
  const { data: employeeProfile } = useEmployeeProfilePhoenix({});

  const { setRedirectUrl } = useRedirectUrlContext();

  useEffect(() => {
    setRedirectUrl(() => (nursingHomeId: string) => {
      return checkIfUserHasAccess(employeeProfile, nursingHomeId)
        ? url
        : nhAppMainPages.DASHBOARD;
    });
  }, [employeeProfile, setRedirectUrl]);
}
